import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Upload, FileText, FilePlus, Loader2, ArrowRight, Briefcase, AlertOctagon, Linkedin } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { evaluateResume } from "@/lib/api";
import LanguagePicker from "@/components/LanguagePicker";
import Footer from "@/components/Footer";

export default function Evaluate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialMode = params.get("tab") === "linkedin" ? "linkedin" : "upload";
  const [mode, setMode] = useState(initialMode);
  const [file, setFile] = useState(null);
  const [resumeText, setResumeText] = useState("");
  const [linkedinText, setLinkedinText] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [lang, setLang] = useState(() => localStorage.getItem("rl_lang") || "en");
  const [loading, setLoading] = useState(false);
  const [limitError, setLimitError] = useState(null);
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    const ok = /\.(pdf|docx|txt)$/i.test(f.name);
    if (!ok) {
      toast.error("Unsupported file", { description: "Please upload a PDF, DOCX or TXT file." });
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large", { description: "Max 10MB." });
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const submit = async () => {
    if (mode === "upload" && !file) {
      toast.error("No file selected", { description: "Upload a resume PDF, DOCX or TXT." });
      return;
    }
    if (mode === "paste" && resumeText.trim().length < 50) {
      toast.error("Resume too short", { description: "Paste your full resume text." });
      return;
    }
    if (mode === "linkedin" && linkedinText.trim().length < 50) {
      toast.error("Profile too short", { description: "Paste your full LinkedIn profile text." });
      return;
    }
    setLoading(true);
    setLimitError(null);
    try {
      const result = await evaluateResume({
        file: mode === "upload" ? file : null,
        resumeText: mode === "paste" ? resumeText : mode === "linkedin" ? linkedinText : "",
        linkedinUrl: mode === "linkedin" ? linkedinUrl : undefined,
        jobDescription,
        targetRole,
        sourceType: mode === "linkedin" ? "linkedin" : "resume",
        lang,
      });
      toast.success("Audit complete", { description: `ResumeLab Score: ${result.overall_score}/100` });
      navigate(`/results/${result.id}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message || "Something went wrong.";
      if (status === 402) {
        setLimitError(detail);
        toast.error("Free tier limit reached", {
          description: detail,
          duration: 10000,
          action: { label: "Upgrade", onClick: () => navigate("/pricing") },
        });
      } else {
        toast.error("Evaluation failed", { description: detail });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 text-zinc-900" data-testid="evaluate-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
        <div className="overline mb-4">◷ NEW AUDIT</div>
        <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tight leading-[1.0] mb-4">
          Let's score your resume.
        </h1>
        <p className="text-zinc-600 text-lg max-w-2xl mb-12">
          Upload your resume or paste the text. Optionally drop in a job description for targeted analysis.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: Resume input */}
          <div className="lg:col-span-7">
            <div className="border border-zinc-200 p-6 lg:p-8">
              <div className="overline mb-5">01 · RESUME</div>
              <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="rounded-none bg-zinc-100 h-auto p-0 grid grid-cols-3 sm:inline-flex w-full sm:w-auto border border-zinc-200">
                  <TabsTrigger value="upload" data-testid="tab-upload" className="rounded-none px-2 sm:px-5 py-2.5 text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none">
                    <Upload className="w-4 h-4 mr-1.5 sm:mr-2" /> Upload
                  </TabsTrigger>
                  <TabsTrigger value="paste" data-testid="tab-paste" className="rounded-none px-2 sm:px-5 py-2.5 text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none">
                    <FileText className="w-4 h-4 mr-1.5 sm:mr-2" /> Paste
                  </TabsTrigger>
                  <TabsTrigger value="linkedin" data-testid="tab-linkedin" className="rounded-none px-2 sm:px-5 py-2.5 text-xs sm:text-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none">
                    <Linkedin className="w-4 h-4 mr-1.5 sm:mr-2" /> LinkedIn
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="upload" className="mt-6">
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    onClick={() => fileRef.current?.click()}
                    data-testid="dropzone"
                    className={`border-2 border-dashed cursor-pointer transition-colors p-12 text-center ${
                      dragging ? "border-[#DC1C22] bg-blue-50/40" : "border-zinc-300 hover:border-zinc-500 hover:bg-zinc-50"
                    }`}
                  >
                    <FilePlus className="w-10 h-10 mx-auto text-zinc-400 mb-4" strokeWidth={1.5} />
                    {file ? (
                      <>
                        <div className="font-medium text-zinc-900" data-testid="file-name">{file.name}</div>
                        <div className="text-xs text-zinc-500 mt-1">{(file.size / 1024).toFixed(1)} KB · click to replace</div>
                      </>
                    ) : (
                      <>
                        <div className="font-medium">Drop your resume here</div>
                        <div className="text-sm text-zinc-500 mt-1">or click to browse — PDF, DOCX, TXT (max 10MB)</div>
                      </>
                    )}
                    <input
                      ref={fileRef}
                      type="file"
                      hidden
                      accept=".pdf,.docx,.txt"
                      onChange={(e) => handleFile(e.target.files?.[0])}
                      data-testid="file-input"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="paste" className="mt-6">
                  <textarea
                    value={resumeText}
                    onChange={(e) => setResumeText(e.target.value)}
                    placeholder="Paste your full resume text here..."
                    rows={14}
                    data-testid="resume-textarea"
                    className="w-full font-mono text-sm border border-zinc-200 p-4 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-y"
                  />
                  <div className="text-xs text-zinc-500 mt-2">{resumeText.length} characters</div>
                </TabsContent>

                <TabsContent value="linkedin" className="mt-6">
                  <div className="mb-3 text-xs text-zinc-600 bg-blue-50 border border-blue-100 p-3 flex items-start gap-2">
                    <Linkedin className="w-4 h-4 text-[#0A66C2] mt-0.5 shrink-0" />
                    <span>
                      Paste your <strong>LinkedIn profile URL</strong> below, then paste your <strong>Headline, About, Experience, and Skills</strong> sections. We&apos;ll audit your profile the way a recruiter searches it.
                    </span>
                  </div>
                  <input
                    type="url"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    placeholder="https://www.linkedin.com/in/your-handle/"
                    data-testid="linkedin-url-input"
                    className="w-full text-sm border border-zinc-200 p-3 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  <textarea
                    value={linkedinText}
                    onChange={(e) => setLinkedinText(e.target.value)}
                    placeholder="Paste your full LinkedIn profile text here (headline, about, experience, skills)..."
                    rows={12}
                    data-testid="linkedin-textarea"
                    className="w-full font-mono text-sm border border-zinc-200 p-4 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-y"
                  />
                  <div className="text-xs text-zinc-500 mt-2">{linkedinText.length} characters</div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Right: Targeting */}
          <div className="lg:col-span-5">
            <div className="border border-zinc-200 p-6 lg:p-8 mb-6">
              <div className="overline mb-5">02 · TARGETING <span className="ml-1 text-zinc-400 normal-case tracking-normal text-[10px]">(optional but recommended)</span></div>
              <label className="block">
                <span className="text-sm font-medium text-zinc-700">Target role</span>
                <input
                  type="text"
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value)}
                  placeholder="e.g. Senior Product Manager, Series B SaaS"
                  data-testid="target-role-input"
                  className="mt-2 w-full border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </label>
              <label className="block mt-5">
                <span className="text-sm font-medium text-zinc-700">Job description</span>
                <textarea
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Paste the JD to enable keyword & skill gap analysis..."
                  rows={8}
                  data-testid="jd-textarea"
                  className="mt-2 w-full font-mono text-xs border border-zinc-200 p-3 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-y"
                />
              </label>
              <div className="mt-6 pt-5 border-t border-zinc-100">
                <LanguagePicker
                  value={lang}
                  onChange={(c) => { setLang(c); localStorage.setItem("rl_lang", c); }}
                  label="Audit & rewrite language"
                  testIdPrefix="evaluate-lang"
                />
                <p className="mt-2 text-[11px] text-zinc-500 leading-snug">
                  GPT-5.2 will write all feedback, AI rewrites and the cover letter in this language.
                </p>
              </div>
            </div>

            <button
              onClick={submit}
              disabled={loading}
              data-testid="submit-evaluation"
              className="w-full btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white disabled:bg-zinc-300 disabled:cursor-not-allowed py-4 text-base"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> ResumeLab is analyzing...
                </>
              ) : (
                <>
                  Run audit <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            {limitError && (
              <div data-testid="limit-error-banner" className="mt-4 border border-red-200 bg-red-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertOctagon className="w-5 h-5 text-[#FF2A00] mt-0.5 shrink-0" strokeWidth={1.75} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-zinc-900">Free tier limit reached</div>
                    <p className="text-sm text-zinc-600 mt-1">{limitError}</p>
                    <Link
                      to="/pricing"
                      data-testid="limit-upgrade-cta"
                      className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-[#DC1C22] hover:bg-[#A8141A] text-white text-xs font-medium"
                    >
                      Upgrade to Pro <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            )}
            <p className="text-xs text-zinc-500 mt-3 text-center">
              Average analysis time: 12 seconds. Powered by GPT-5.2.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
